const slugify = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-+|-+$)/g, '').slice(0, 50);

/** Pass shape stored on a form: name, price and currency. Event-only details stay on the event. */
export const passId = (pass) => pass?.id || slugify(pass?.name) || 'pass';

/** A display name can repeat. The link cannot, so the second one borrows its category. */
export function passIdFor(passes, index, name, groupId) {
  const used = new Set();
  (passes || []).forEach((pass, i) => { if (i !== index && pass?.id) used.add(pass.id); });
  const base = slugify(name) || 'pass';
  if (!used.has(base)) return base;
  const grouped = slugify(`${groupId || ''} ${name}`);
  if (grouped && grouped !== base && !used.has(grouped)) return grouped;
  let n = 2;
  while (used.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

/** Split a shared link and keep every field that was ticked for it on both passes. */
export function withUniquePassIds(schema) {
  const before = schema?.passes || [];
  const passes = assignPassIds(before);
  const aliases = new Map();
  before.forEach((pass, index) => {
    const oldId = pass?.id || passId(pass);
    const nextId = passes[index]?.id;
    if (!oldId || !nextId || oldId === nextId) return;
    const list = aliases.get(oldId) || [oldId];
    if (!list.includes(nextId)) list.push(nextId);
    aliases.set(oldId, list);
  });
  if (!aliases.size) return { ...schema, passes };
  const expand = (ids) => (ids || []).flatMap((id) => aliases.get(id) || [id]);
  return {
    ...schema,
    passes,
    fields: (schema.fields || []).map((field) => ({ ...field, passIds: expand(field.passIds) })),
  };
}

export function assignPassIds(passes = []) {
  const assigned = [];
  return (passes || []).map((pass) => {
    const taken = assigned.some((item) => item.id === pass?.id);
    const id = pass?.id && !taken ? pass.id : passIdFor(assigned, assigned.length, pass?.name, pass?.groupId);
    const next = id === pass?.id ? pass : { ...pass, id };
    assigned.push(next);
    return next;
  });
}

export const emptyPass = () => ({ name: '', type: 'PAID', price: 0, currency: 'INR', description: '', groupId: '' });

export const emptyCategory = () => ({ name: '', type: 'PAID', description: '' });

/**
 * The form's pass list becomes the event's pass list. Matching ids keep capacity, sale dates and icons.
 */
export function mergePasses(existing = [], fromForm = []) {
  const byId = new Map((existing || []).map((pass) => [passId(pass), pass]));
  return assignPassIds((fromForm || []).filter((pass) => pass?.name?.trim())).map((pass) => {
    const id = pass.id;
    const current = byId.get(id) || {};
    return {
      ...current,
      id,
      name: pass.name.trim(),
      type: pass.type || current.type || 'PAID',
      price: pass.type === 'PAID' ? Number(pass.price) || 0 : null,
      currency: pass.type === 'PAID' ? (pass.currency || 'INR') : null,
      description: pass.description ?? current.description ?? '',
      groupId: pass.groupId || current.groupId || null,
      benefits: current.benefits || [],
      maxPerBooking: current.maxPerBooking || 1,
    };
  });
}

/** Form categories replace the event categories. Day access and icons stay on the matching category. */
export function mergeGroups(existing = [], fromForm = []) {
  const named = (fromForm || []).filter((group) => group?.name?.trim());
  if (!named.length) return existing || [];
  const byId = new Map((existing || []).map((group) => [passId(group), group]));
  return named.map((group) => {
    const id = passId(group);
    const current = byId.get(id) || {};
    return {
      ...current,
      id,
      name: group.name.trim(),
      type: group.type || current.type || 'PAID',
      description: group.description ?? current.description ?? '',
      dayAccess: current.dayAccess || [],
    };
  });
}

export async function syncEventPasses(api, eventId, passes, groups) {
  if (!eventId) return null;
  const current = await api.getTicketingConfig(eventId);
  const next = { ...current };
  if (Array.isArray(passes)) next.passes = mergePasses(current?.passes, passes);
  if (Array.isArray(groups) && groups.some((group) => group?.name?.trim())) {
    next.groups = mergeGroups(current?.groups, groups);
  }
  return api.updateTicketingConfig(eventId, next);
}
